import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import {
  Button, Card, Select, Table, EmptyState, Modal, useToast,
} from '../../../design-system';
import {
  money, day, dayTime, REPORT_LABEL, REPORT_TYPES, isBillReport,
} from '../../../lib/tallyBooks';
import '../../styles/TallyPanel.css';

// Reports Tally computes for itself — trial balance, receivables, payables.
//
// ⚠️ ASKED OF TALLY, NEVER SUMMED FROM OUR OWN MIRROR. We hold the vouchers and
// could total them; the entire value of putting a trial balance in front of a
// preparer is that it matches the client's own screen to the rupee, and a
// figure of ours that disagrees by one costs more time than the report saves.
// The server says the same thing at the top of the report section in
// tallyController.js — this is the view onto it.
//
// ⚠️ ASKING IS ASYNCHRONOUS AND CANNOT NOT BE. The agent polls, so the round
// trip is bounded by its poll interval plus however long Tally takes to build
// the report on a PC somebody else is working on. The request returns a job id
// and this polls it, exactly as company discovery does.

const POLL_MS = 2000;
const DEADLINE_MS = 120000;

function TallyReports({ connectionId, connectionStatus: status }) {
  const toast = useToast();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState('trial_balance');
  const [running, setRunning] = useState(false);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/api/tally/connections/${connectionId}/reports`);
      setReports(res.data?.reports || []);
    } catch {
      // Leave the list as it stands rather than blanking it on a blip.
    } finally {
      setLoading(false);
    }
  }, [connectionId]);

  useEffect(() => { load(); }, [load]);

  /**
   * Queue one, then wait for it.
   *
   * ⚠️ THE JOB'S OWN STATUS TELLS TWO DIFFERENT FAILURES APART, the same way
   * the connect flow's discovery does. Still 'queued' at the deadline means
   * nothing on that machine ever claimed it — the sync service is not running
   * there and Tally was never contacted, so "is Tally open?" is the wrong
   * question to send somebody away with.
   */
  const request = async () => {
    setRunning(true);
    try {
      const { data } = await apiClient.post(
        `/api/tally/connections/${connectionId}/reports`,
        { reportType },
      );
      const deadline = Date.now() + DEADLINE_MS;
      let last = 'queued';

      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, POLL_MS));
        // eslint-disable-next-line no-await-in-loop
        const res = await apiClient.get(`/api/tally/jobs/${data.jobId}`);
        last = res.data.status;
        if (last === 'succeeded') {
          toast.success(`${REPORT_LABEL[reportType]} is ready.`);
          await load();
          break;
        }
        if (last === 'failed' || last === 'expired') {
          toast.danger(res.data.error || 'Tally could not produce that report');
          break;
        }
        if (Date.now() > deadline) {
          toast.danger(
            last === 'queued'
              ? 'Nothing on that machine picked this up — the Tally sync service is not running there.'
              : 'The agent asked Tally and it has not answered yet. The report may still land; check back shortly.',
          );
          break;
        }
      }
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not request that report');
    } finally {
      setRunning(false);
    }
  };

  const view = async (id) => {
    try {
      const res = await apiClient.get(`/api/tally/connections/${connectionId}/reports/${id}`);
      setOpen(res.data?.report || null);
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not open that report');
    }
  };

  return (
    <Card>
      <div className="tally-head">
        <div>
          <h3 className="tally-section-title">Reports</h3>
          <p className="tally-muted">
            Computed by Tally itself, not by us — so the figures match what the
            client sees on their own screen. Each one replaces the last for the
            same period.
          </p>
        </div>
        <div className="tally-report-pick">
          <Select
            label=""
            aria-label="Report"
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
          >
            {REPORT_TYPES.map((t) => <option key={t} value={t}>{REPORT_LABEL[t]}</option>)}
          </Select>
          {/* Refused server-side on a paused or revoked connection, so it is
              disabled here rather than failing with a 409 after the click. */}
          <Button onClick={request} loading={running} disabled={running || status !== 'active'}>
            {running ? 'Asking Tally…' : 'Get from Tally'}
          </Button>
        </div>
      </div>

      {status !== 'active' && (
        <p className="tally-warn">
          {`This connection is ${status}. Tally will not be asked for anything until it is active again.`}
        </p>
      )}

      {loading && !reports.length ? (
        <p className="tally-muted">Loading…</p>
      ) : !reports.length ? (
        <EmptyState
          icon="📊"
          title="No reports yet"
          description="Pick one above and ask Tally for it. It runs on the client's machine, so Tally must be open with the company loaded."
        />
      ) : (
        <Table
          columns={[
            {
              key: 'type',
              header: 'Report',
              render: (r) => REPORT_LABEL[r.reportType] || r.reportType,
            },
            {
              key: 'period',
              header: 'Period',
              // A bill report is a position AS AT a date, not a movement over
              // one — the server stores both ends equal for those, so printing
              // a range would invent a window nobody asked for.
              render: (r) => (isBillReport(r.reportType)
                ? `as at ${day(r.periodTo)}`
                : `${day(r.periodFrom)} – ${day(r.periodTo)}`),
            },
            {
              key: 'total',
              header: 'Total',
              render: (r) => (
                <span className="tally-num">
                  {isBillReport(r.reportType) ? money(r.totalAmount) : money(r.totalDebit)}
                </span>
              ),
            },
            // ⚠️ WHEN TALLY COMPUTED IT, not when we asked. A figure without its
            // age is the failure this whole module is engineered against.
            { key: 'synced', header: 'Computed', render: (r) => dayTime(r.syncedAt) },
            {
              key: 'open',
              header: '',
              render: (r) => (
                <Button size="sm" variant="secondary" onClick={() => view(r._id)}>
                  Open
                </Button>
              ),
            },
          ]}
          data={reports}
          keyField="_id"
        />
      )}

      <ReportModal report={open} onClose={() => setOpen(null)} />
    </Card>
  );
}

/** One stored snapshot, with its rows. */
function ReportModal({ report, onClose }) {
  if (!report) return null;
  const bills = isBillReport(report.reportType);

  return (
    <Modal isOpen onClose={onClose} title={REPORT_LABEL[report.reportType] || report.reportType}>
      <p className="tally-muted">
        {bills ? `as at ${day(report.periodTo)}` : `${day(report.periodFrom)} – ${day(report.periodTo)}`}
        {` · computed by Tally ${dayTime(report.syncedAt)}`}
      </p>

      {bills ? (
        <>
          {!report.bills?.length ? (
            <p className="tally-muted">Tally reported nothing outstanding.</p>
          ) : (
            <Table
              columns={[
                { key: 'party', header: 'Party', render: (b) => b.partyName || '—' },
                { key: 'ref', header: 'Bill', render: (b) => b.billRef || '—' },
                { key: 'date', header: 'Dated', render: (b) => day(b.billDate) },
                { key: 'due', header: 'Due', render: (b) => day(b.dueDate) },
                // Tally's own ageing, applying the party's credit terms — which
                // we do not hold. Deriving it here would differ from the
                // client's own ageing report by exactly what we do not know.
                { key: 'age', header: 'Overdue', render: (b) => (b.overdueDays ? `${b.overdueDays}d` : '—') },
                {
                  key: 'amt',
                  header: 'Amount',
                  render: (b) => <span className="tally-num">{money(b.amount)}</span>,
                },
              ]}
              data={(report.bills || []).map((b, i) => ({ ...b, _k: `b${i}` }))}
              keyField="_k"
            />
          )}
          <p className="tally-total">{`Total ${money(report.totalAmount)}`}</p>
        </>
      ) : (
        <>
          {!report.rows?.length ? (
            <p className="tally-muted">Tally returned no rows for this period.</p>
          ) : (
            <Table
              columns={[
                { key: 'name', header: 'Account', render: (r) => r.name || '—' },
                { key: 'parent', header: 'Under', render: (r) => r.parent || '—' },
                {
                  key: 'debit',
                  header: 'Debit',
                  render: (r) => <span className="tally-num">{money(r.debit)}</span>,
                },
                {
                  key: 'credit',
                  header: 'Credit',
                  render: (r) => <span className="tally-num">{money(r.credit)}</span>,
                },
                {
                  key: 'closing',
                  header: 'Closing',
                  render: (r) => <span className="tally-num">{money(r.closingBalance)}</span>,
                },
              ]}
              data={(report.rows || []).map((r, i) => ({ ...r, _k: `r${i}` }))}
              keyField="_k"
            />
          )}
          {/* ⚠️ THE STORED TOTALS, WHICH WERE SUMMED FROM THE ROWS ON INGEST —
              not re-added here and not taken from Tally's own total line. If
              a row failed to parse the two columns will not agree, and that
              disagreement being VISIBLE is the honest outcome. */}
          <p className="tally-total">
            {`Debit ${money(report.totalDebit)} · Credit ${money(report.totalCredit)}`}
          </p>
        </>
      )}
    </Modal>
  );
}

export default TallyReports;
